import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Clock, User as UserIcon, Calendar, Download, FileText } from 'lucide-react';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import getLoansList from '../api/GetLoansList';
import { setCurrentMember } from '../store/slices/membersSlice';

const DEFAULT_COVER = '/images/default_cover_w299.jpg';

export default function BorrowedBooksModal({ isOpen, onClose, onNavigate }) {
    const [borrowedLoans, setBorrowedLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const currentUser = useSelector((state) => state.user.currentUser);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const delaiEmprunt = useSelector((state) => state.user.delaiEmprunt);
    const { items: books } = useSelector((state) => state.books);
    const { items: members } = useSelector((state) => state.members);

    useEffect(() => {
        const fetchBorrowedLoans = async () => {
            if (!isOpen) return;
            setLoading(true);
            try {
                let loans = await getLoansList(API_BASE, token, code_etablissement);

                // If student, only show personal loans
                if (currentUser.role === 'Étudiant') {
                    const myId = currentUser.id || currentUser.ID;
                    loans = loans.filter(loan => loan.user_id == myId);
                }

                // Filter for active loans (not returned)
                const activeLoans = loans.filter(loan =>
                    loan.end_date.startsWith("0001")
                ).map(loan => {
                    const member = members.find(m => (m.ID || m.id) == loan.user_id);
                    const book = books.find(b => (b.item_id || b.id) == loan.item_id);

                    if (book?.status === 'Réservé') return null;

                    const startDate = new Date(loan.start_date);
                    const dueDate = new Date(startDate.getTime() + delaiEmprunt * 24 * 60 * 60 * 1000);
                    const now = new Date();
                    const isLate = now > dueDate;

                    return {
                        loan,
                        member,
                        book,
                        startDate,
                        dueDate,
                        isLate
                    };
                }).filter(item => item !== null)
                    .sort((a, b) => b.startDate - a.startDate);

                setBorrowedLoans(activeLoans);
            } catch (error) {
                console.error("Failed to fetch borrowed loans:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchBorrowedLoans();
    }, [isOpen, API_BASE, token, code_etablissement, delaiEmprunt, books, members, currentUser]);

    const handleMemberClick = (member) => {
        if (member) {
            dispatch(setCurrentMember(member));
            onClose();
            if (onNavigate) {
                onNavigate('Members');
            }
        }
    };

    const handleExportCSV = () => {
        if (borrowedLoans.length === 0) return;

        const studentsMap = new Map();

        borrowedLoans.forEach(({ loan, member, book, startDate, dueDate, isLate }) => {
            const memberId = member?.ID || member?.id || 'Inconnu';
            if (!studentsMap.has(memberId)) {
                studentsMap.set(memberId, {
                    classe: member?.classe || 'Sans classe',
                    nom: member?.name || 'Inconnu',
                    livres: []
                });
            }

            const studentInfo = studentsMap.get(memberId);
            const titre = book?.title || 'Livre inconnu';
            const dateEmprunt = startDate.toLocaleDateString();
            const dateRetour = dueDate.toLocaleDateString();

            const statut = isLate ? '(En retard)' : '(En cours)';
            studentInfo.livres.push(`${titre} [Emprunté le ${dateEmprunt}, Retour prévu le ${dateRetour}] ${statut}`);
        });

        const exportData = Array.from(studentsMap.values())
            .sort((a, b) => {
                if (a.classe === b.classe) {
                    return a.nom.localeCompare(b.nom);
                }
                return a.classe.localeCompare(b.classe);
            })
            .map(student => ({
                'Classe': student.classe,
                'Élève': student.nom,
                'Livres empruntés': student.livres.join(' | ')
            }));

        const csv = Papa.unparse(exportData, { delimiter: ';' });
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `emprunts_bibliotheque_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportPDF = () => {
        if (borrowedLoans.length === 0) return;

        const doc = new jsPDF();
        const classMap = new Map();

        borrowedLoans.forEach(({ loan, member, book, startDate, dueDate, isLate }) => {
            const className = member?.classe || 'Sans classe';
            const memberId = member?.ID || member?.id || 'Inconnu';

            if (!classMap.has(className)) {
                classMap.set(className, new Map());
            }

            const studentsInClass = classMap.get(className);

            if (!studentsInClass.has(memberId)) {
                studentsInClass.set(memberId, {
                    nom: member?.name || 'Inconnu',
                    livres: []
                });
            }

            const studentInfo = studentsInClass.get(memberId);
            const titre = book?.title || 'Livre inconnu';
            const dateEmprunt = startDate.toLocaleDateString();
            const dateRetour = dueDate.toLocaleDateString();

            const statut = isLate ? '(En retard)' : '';
            studentInfo.livres.push(`${titre}\n[Emprunt: ${dateEmprunt}, Retour: ${dateRetour}] ${statut}`.trim());
        });

        const sortedClasses = Array.from(classMap.keys()).sort();

        sortedClasses.forEach((className, index) => {
            if (index > 0) {
                doc.addPage();
            }

            doc.setFontSize(18);
            doc.setTextColor(40, 40, 40);
            doc.text(`Livres empruntés - Classe : ${className}`, 14, 22);
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Généré le ${new Date().toLocaleDateString()}`, 14, 30);

            const studentsMap = classMap.get(className);
            const tableData = Array.from(studentsMap.values())
                .sort((a, b) => a.nom.localeCompare(b.nom))
                .map(student => [
                    student.nom,
                    student.livres.join('\n\n')
                ]);

            autoTable(doc, {
                startY: 35,
                head: [['Élève', 'Livres empruntés']],
                body: tableData,
                theme: 'grid',
                styles: {
                    fontSize: 10,
                    cellPadding: 5,
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [16, 185, 129], // Emerald-500 equivalent color
                    textColor: 255,
                    fontStyle: 'bold'
                },
                columnStyles: {
                    0: { cellWidth: 60, fontStyle: 'bold' },
                    1: { cellWidth: 'auto' }
                },
                didDrawPage: function (data) {
                    const str = 'Page ' + doc.internal.getNumberOfPages();
                    doc.setFontSize(10);
                    const pageSize = doc.internal.pageSize;
                    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                    doc.text(str, data.settings.margin.left, pageHeight - 10);
                }
            });
        });

        doc.save(`emprunts_bibliotheque_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm lg:pl-72">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
                >
                    <div className="p-6 border-b border-white/10 flex items-center justify-between bg-emerald-500/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400">
                                <BookOpen size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Livres Empruntés</h3>
                                <p className="text-sm text-slate-400">{borrowedLoans.length} emprunt{borrowedLoans.length > 1 ? 's' : ''} en cours</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {borrowedLoans.length > 0 && currentUser?.role === 'Bibliothécaire' && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleExportCSV}
                                        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border border-white/10"
                                        title="Exporter en CSV (Excel)"
                                    >
                                        <Download size={16} />
                                        CSV
                                    </button>
                                    <button
                                        onClick={handleExportPDF}
                                        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border border-white/10"
                                        title="Télécharger en PDF (Trié par classe)"
                                    >
                                        <FileText size={16} />
                                        PDF
                                    </button>
                                </div>
                            )}
                            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-full">
                                <X size={24} />
                            </button>
                        </div>
                    </div>

                    <div className="p-6 overflow-y-auto custom-scrollbar">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20 gap-4">
                                <div className="w-10 h-10 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                                <p className="text-slate-400 animate-pulse">Chargement des emprunts...</p>
                            </div>
                        ) : borrowedLoans.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {borrowedLoans.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="flex gap-4 bg-slate-800/20 p-4 rounded-xl border border-white/5 hover:bg-slate-800/40 hover:border-emerald-500/30 transition-all group relative"
                                    >
                                        <img
                                            src={item.book?.cover || DEFAULT_COVER}
                                            alt={item.book?.title || "Livre"}
                                            className="w-16 h-24 object-cover rounded-md shadow-lg"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = DEFAULT_COVER;
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-white text-base truncate mb-1 group-hover:text-emerald-400 transition-colors">
                                                {item.book?.title || "Livre inconnu"}
                                            </h4>

                                            <div
                                                onClick={() => handleMemberClick(item.member)}
                                                className="flex items-center gap-2 mb-2 p-1.5 -ml-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors w-fit"
                                            >
                                                <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                                                    {item.member?.avatar ? (
                                                        <img src={item.member.avatar} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <UserIcon size={12} className="text-slate-400" />
                                                    )}
                                                </div>
                                                <span className="text-sm text-slate-300 font-medium truncate">{item.member?.name || "Membre inconnu"}</span>
                                            </div>

                                            <div className="space-y-1.5">
                                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                                    <Calendar size={12} className="text-slate-500" />
                                                    <span>Le {item.startDate.toLocaleDateString()}</span>
                                                </div>
                                                <div className={`flex items-center gap-2 text-xs font-medium px-2 py-1 rounded-md w-fit ${item.isLate ? 'bg-red-500/10 text-red-400' : 'bg-slate-700/50 text-slate-300'}`}>
                                                    <Clock size={12} />
                                                    <span>Retour: {item.dueDate.toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-slate-800/10 rounded-2xl border border-dashed border-white/5">
                                <BookOpen className="mx-auto text-slate-700 mb-4" size={48} />
                                <p className="text-slate-400 text-lg">Aucun livre n'est actuellement emprunté</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
